import { useCallback, useEffect, useState } from 'react';

import useBoolean from '../../../hooks/use-boolean';
import { passwordRules } from '../../validations';

import { PasswordRuleType, PasswordStatus } from './type';

export const useValidatedPassword = (initialValue?: string) => {
  const { value: hasBeenTouched, setTrue: touched } = useBoolean(false);
  const { value: isFocused, setTrue: setFocused, setFalse: setBlurred } = useBoolean(false);
  const [state, setState] = useState<Record<PasswordRuleType, PasswordStatus>>({
    upperCase: 'stale',
    lowerCase: 'stale',
    number: 'stale',
    symbol: 'stale',
    length: 'stale',
  });

  const onChange = useCallback((value: string) => {
    setState({
      length: passwordRules.length(value) ? 'valid' : 'stale',
      upperCase: passwordRules.upperCase(value) ? 'valid' : 'stale',
      lowerCase: passwordRules.lowerCase(value) ? 'valid' : 'stale',
      number: passwordRules.number(value) ? 'valid' : 'stale',
      symbol: passwordRules.symbol(value) ? 'valid' : 'stale',
    });
  }, []);

  // values are stored in localStorage in web-pro-signup package
  // without this useEffect, the state corresponds to an initialValue of undefined at the first render
  useEffect(() => {
    if (initialValue) {
      onChange(initialValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const invalidToStaleState = () => {
    setState((prev) => ({
      length: prev.length === 'invalid' ? 'stale' : prev.length,
      upperCase: prev.upperCase === 'invalid' ? 'stale' : prev.upperCase,
      lowerCase: prev.lowerCase === 'invalid' ? 'stale' : prev.lowerCase,
      number: prev.number === 'invalid' ? 'stale' : prev.number,
      symbol: prev.symbol === 'invalid' ? 'stale' : prev.symbol,
    }));
  };

  useEffect(() => {
    if (isFocused) {
      invalidToStaleState();
    }
  }, [isFocused]);

  const invalidateState = useCallback((callback: (newState: Record<PasswordRuleType, PasswordStatus>) => void) => {
    setState((prev) => {
      const newState = {
        length: prev.length === 'stale' ? 'invalid' : prev.length,
        upperCase: prev.upperCase === 'stale' ? 'invalid' : prev.upperCase,
        lowerCase: prev.lowerCase === 'stale' ? 'invalid' : prev.lowerCase,
        number: prev.number === 'stale' ? 'invalid' : prev.number,
        symbol: prev.symbol === 'stale' ? 'invalid' : prev.symbol,
      };
      callback(newState);
      return newState;
    });
  }, []);

  const onFocus = useCallback(() => {
    touched();
    setFocused();
  }, [touched, setFocused]);

  const areAllRequirementsMet = Object.values(state).every((rule) => rule === 'valid');
  const isRulesVisible = (!areAllRequirementsMet && hasBeenTouched) || isFocused;

  return {
    state,
    invalidateState,
    onChange,
    onFocus,
    isFocused,
    setBlurred,
    isRulesVisible,
    hasBeenTouched,
    areAllRequirementsMet,
  };
};
